var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
if (!customElements.get("ona-image-slot")) {
  class OnaImageSlot extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.img = document.createElement("img");
      this.img.decoding = "async";
      this.img.style.opacity = "0";
      this.img.style.transition = "opacity var(--ona-image-slot-transition-duration, 300ms) ease";
      this.img.onload = () => this._onImageLoad();
      this.img.onerror = () => this._onImageError();
      const style = document.createElement("style");
      style.textContent = `
        :host {
          position: relative;
          display: flex;
          width: 100%;
          aspect-ratio: var(--ona-image-slot-aspect-ratio, 16 / 9);
          background: var(--ona-image-slot-background-color, #f0f0f0);
          overflow: hidden;
          border-radius: inherit;
        }

        :host([background]) {
          position: absolute;
          inset: 0;
          z-index: var(--ona-image-slot-background-zindex, -1);
          aspect-ratio: auto;
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: var(--ona-image-slot-object-fit, cover);
          object-position: var(--ona-image-slot-focal-point, center);
          display: block;
          border-radius: inherit;
          transition: opacity var(--ona-image-slot-transition-duration, 300ms) ease;
        }

        .loader,
        .fallback {
          position: absolute;
          inset: 0;
          display: grid;
          place-content: center;
          background: inherit;
          z-index: 1;
          pointer-events: none;
        }

        ::slotted([slot="loader"]),
        ::slotted([slot="fallback"]) {
          width: 100%;
          height: 100%;
          display: grid;
          place-content: center;
        }

        .default-error {
          font-size: 0.85em;
          color: var(--ona-image-slot-error-color, #900);
          text-align: center;
        }

        @media (prefers-reduced-motion: reduce) {
          img { transition: none !important; }
        }

        :host([debug]) {
          outline: 1px dashed var(--ona-image-slot-debug-outline, red);
        }

        .debug-panel {
          position: absolute;
          bottom: 0;
          left: 0;
          font-family: monospace;
          font-size: 10px;
          background: var(--ona-image-slot-debug-bg, rgba(255,255,255,0.9));
          color: var(--ona-image-slot-debug-color, #000);
          padding: 2px 4px;
          z-index: 10;
          pointer-events: none;
          white-space: pre;
          border-top-right-radius: 4px;
        }
      `;
      this.loader = document.createElement("div");
      this.loader.className = "loader";
      this.loader.innerHTML = `
        <slot name="loader">
          <ona-spinner></ona-spinner>
        </slot>
      `;
      this.fallback = document.createElement("div");
      this.fallback.className = "fallback";
      this.fallback.style.display = "none";
      this.fallback.innerHTML = `
        <slot name="fallback">
          <div class="default-error">\u26A0\uFE0F Image failed to load</div>
        </slot>
      `;
      this.debugPanel = document.createElement("div");
      this.debugPanel.className = "debug-panel";
      this.debugPanel.style.display = "none";
      this.shadowRoot.append(style, this.img, this.loader, this.fallback, this.debugPanel);
      this._resolveLoad = null;
      this._rejectLoad = null;
      this._timeout = null;
    }
    connectedCallback() {
      if (window.__ONA_DEBUG__ && !this.hasAttribute("debug")) {
        this.setAttribute("debug", "");
      }
      this._updateAttributes();
      this._updateDebug();
      if (!this.img.complete) {
        this._dispatch("loadstart");
      }
      this._timeout = setTimeout(() => {
        if (!this.img.complete) {
          this.loader.style.display = "grid";
        }
      }, 500);
    }
    attributeChangedCallback(name) {
      if (["debug", "background"].includes(name)) {
        this._updateDebug();
      }
      this._updateAttributes();
    }
    _updateAttributes() {
      this.img.srcset = this.getAttribute("srcset") || "";
      this.img.sizes = this.getAttribute("sizes") || "";
      this.img.alt = this.getAttribute("alt") || "";
      this.img.loading = this.getAttribute("loading") || "lazy";
      this.img.draggable = false;
      this.img.part = "image";
      if (this.getAttribute("focal-point")) {
        this.img.style.setProperty("--ona-image-slot-focal-point", this.getAttribute("focal-point"));
      }
    }
    _updateDebug() {
      const isDebug = this.hasAttribute("debug");
      this.debugPanel.style.display = isDebug ? "block" : "none";
      if (isDebug) {
        const state = this.img.complete ? this.img.naturalWidth ? "loaded" : "error" : "loading";
        this.debugPanel.textContent = `ona-image-slot (debug)
status: ${state}
alt: ${this.img.alt || '""'}
sizes: ${this.img.sizes}
srcset: ${this.img.srcset}`;
      }
    }
    _onImageLoad() {
      clearTimeout(this._timeout);
      this.img.style.opacity = "1";
      this.loader.style.display = "none";
      this._updateDebug();
      this._dispatch("loadsuccess", { img: this.img });
      if (this._resolveLoad) this._resolveLoad(this.img);
      this._resolveLoad = this._rejectLoad = null;
    }
    _onImageError() {
      clearTimeout(this._timeout);
      this.img.style.opacity = "0.3";
      this.loader.style.display = "none";
      this.fallback.style.display = "grid";
      this._updateDebug();
      this._dispatch("loaderror", { error: new Error("Image failed to load") });
      if (this._rejectLoad) this._rejectLoad(new Error("Image failed to load"));
      this._resolveLoad = this._rejectLoad = null;
    }
    _dispatch(name, detail = {}) {
      this.dispatchEvent(
        new CustomEvent(name, {
          detail,
          bubbles: true,
          composed: true
        })
      );
    }
    load() {
      return new Promise((resolve, reject) => {
        if (this.img.complete) {
          if (this.img.naturalWidth) {
            resolve(this.img);
          } else {
            reject(new Error("Image failed to load"));
          }
        } else {
          this._resolveLoad = resolve;
          this._rejectLoad = reject;
        }
      });
    }
  }
  __publicField(OnaImageSlot, "observedAttributes", ["srcset", "sizes", "alt", "loading", "debug", "background"]);
  customElements.define("ona-image-slot", OnaImageSlot);
}
